/**
 * In Memoriam Page - A tribute to cats who have passed
 * Allows owners to submit memorials with admin approval
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Heart, Camera, Upload, Flame, X, Loader2, Check, Cat, 
  Calendar, User, Mail, Sparkles, Search, Star, Filter, ChevronDown
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InMemoriamPage = () => {
  const [memorials, setMemorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showMemorialModal, setShowMemorialModal] = useState(false);
  const [selectedMemorial, setSelectedMemorial] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [featuredMemorial, setFeaturedMemorial] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");
  const [formData, setFormData] = useState({
    cat_name: "",
    birth_year: "",
    passing_year: "",
    passing_date: "",
    tribute_message: "",
    owner_name: "",
    owner_email: "",
    was_catpals_cat: false,
    image_base64: "",
    image_mime_type: ""
  });

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    fetchMemorials();
    fetchFeaturedMemorial();
  }, [page, yearFrom, yearTo]);

  const fetchFeaturedMemorial = async () => {
    try {
      const res = await fetch(`${API_URL}/api/memorials/featured`);
      const data = await res.json();
      if (data && data.memorial) {
        setFeaturedMemorial(data);
      }
    } catch (err) {
      console.error("Failed to fetch featured memorial:", err);
    }
  };

  const fetchMemorials = async () => {
    try {
      let url = `${API_URL}/api/memorials?page=${page}&limit=12`;
      if (yearFrom) url += `&year_from=${yearFrom}`;
      if (yearTo) url += `&year_to=${yearTo}`;
      
      const res = await fetch(url);
      const data = await res.json();
      if (data.memorials) {
        setMemorials(Array.isArray(data.memorials) ? data.memorials : []);
        setPagination(data.pagination);
      } else {
        setMemorials(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Failed to fetch memorials:", err);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setYearFrom("");
    setYearTo("");
    setSearchQuery("");
    setPage(1);
  };

  // Filter memorials by search query (client-side for simplicity)
  const filteredMemorials = searchQuery.trim() 
    ? memorials.filter(m => 
        m.cat_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.owner_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.tribute_message?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : memorials;

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      alert("Image too large. Max size is 10MB.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(",")[1];
      setFormData(prev => ({
        ...prev,
        image_base64: base64,
        image_mime_type: file.type
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.image_base64) {
      alert("Please upload a photo");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/memorials`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        setSubmitSuccess(true);
        setFormData({
          cat_name: "",
          birth_year: "",
          passing_year: "",
          passing_date: "",
          tribute_message: "",
          owner_name: "",
          owner_email: "",
          was_catpals_cat: false,
          image_base64: "",
          image_mime_type: ""
        });
      } else {
        const error = await res.json();
        alert(error.detail || "Failed to submit memorial");
      }
    } catch (err) {
      console.error("Failed to submit:", err);
      alert("Failed to submit memorial");
    } finally {
      setSubmitting(false);
    }
  };

  const [candleAnimating, setCandleAnimating] = useState({});

  const lightCandle = async (memorialId) => {
    try {
      const res = await fetch(`${API_URL}/api/memorials/${memorialId}/candle`, {
        method: "POST"
      });
      
      if (res.status === 429) {
        // Rate limited - show a gentle message
        return;
      }
      
      if (res.ok) {
        // Trigger animation
        setCandleAnimating(prev => ({ ...prev, [memorialId]: true }));
        setTimeout(() => {
          setCandleAnimating(prev => ({ ...prev, [memorialId]: false }));
        }, 1500);
        
        // Update local state
        setMemorials(prev => prev.map(m => 
          m.memorial_id === memorialId ? { ...m, candle_count: (m.candle_count || 0) + 1 } : m
        ));
        if (selectedMemorial?.memorial_id === memorialId) {
          setSelectedMemorial(prev => ({ ...prev, candle_count: (prev.candle_count || 0) + 1 }));
        }
      }
    } catch (err) {
      console.error("Failed to light candle:", err);
    }
  };

  const openMemorial = (memorial) => {
    setSelectedMemorial(memorial);
    setShowMemorialModal(true);
  };

  const formatYears = (birth, passing) => {
    if (birth && passing) return `${birth} - ${passing}`;
    if (passing) return `Passed ${passing}`;
    return "";
  };

  return (
    <div className="min-h-screen pt-24 sm:pt-36 bg-gradient-to-br from-stone-100 via-stone-50 to-stone-100" data-testid="in-memoriam-page">
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-gradient-to-br from-stone-500 to-stone-700 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Flame className="w-10 h-10 text-amber-300" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-800 mb-4">In Memoriam</h1>
          <p className="text-stone-600 max-w-2xl mx-auto text-lg mb-2">
            A loving tribute to friends lost too soon
          </p>
          <p className="text-stone-500 max-w-xl mx-auto">
            This page is dedicated to the memory of beloved cats who have crossed the Rainbow Bridge. 
            Each candle lit here keeps their memory burning bright.
          </p>
        </div>

        {/* Submit Button */}
        <div className="text-center mb-8">
          <Button 
            onClick={() => { setSubmitSuccess(false); setShowSubmitModal(true); }}
            className="bg-stone-700 hover:bg-stone-800 text-white"
            size="lg"
            data-testid="share-memorial-button"
          >
            <Heart className="w-5 h-5 mr-2" />
            Share a Memorial
          </Button>
        </div>

        {/* Featured Memorial Spotlight */}
        {featuredMemorial && featuredMemorial.memorial && (
          <div className="mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
              <h2 className="text-xl font-semibold text-stone-700">Featured This Week</h2>
              <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
            </div>
            <Card 
              className="max-w-2xl mx-auto overflow-hidden bg-gradient-to-br from-amber-50 to-stone-50 border-amber-200 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
              onClick={() => openMemorial(featuredMemorial.memorial)}
            >
              <div className="md:flex">
                <div className="md:w-1/2 aspect-square md:aspect-auto">
                  <img
                    src={`data:${featuredMemorial.memorial.image_mime_type};base64,${featuredMemorial.memorial.image_base64}`}
                    alt={featuredMemorial.memorial.cat_name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="md:w-1/2 p-6 flex flex-col justify-center">
                  {featuredMemorial.memorial.was_catpals_cat && (
                    <Badge className="bg-amber-600 text-white w-fit mb-3">
                      <Cat className="w-3 h-3 mr-1" />
                      CatPals+ Family
                    </Badge>
                  )}
                  <h3 className="text-2xl font-bold text-stone-800 mb-2">
                    {featuredMemorial.memorial.cat_name}
                  </h3>
                  <p className="text-stone-500 text-sm mb-3">
                    {formatYears(featuredMemorial.memorial.birth_year, featuredMemorial.memorial.passing_year)}
                  </p>
                  <p className="text-stone-600 italic line-clamp-3 mb-4">
                    "{featuredMemorial.memorial.tribute_message}"
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-stone-500">by {featuredMemorial.memorial.owner_name}</span>
                    <button 
                      className="flex items-center gap-1 text-amber-600 hover:text-amber-700"
                      onClick={(e) => { e.stopPropagation(); lightCandle(featuredMemorial.memorial.memorial_id); }}
                    >
                      <Flame className={`w-5 h-5 ${candleAnimating[featuredMemorial.memorial.memorial_id] ? 'text-orange-500 scale-125 animate-pulse' : ''}`} />
                      <span>{featuredMemorial.memorial.candle_count || 0}</span>
                    </button>
                  </div>
                </CardContent>
              </div>
            </Card>
          </div>
        )}

        {/* Search and Filters */}
        {memorials.length > 0 && (
          <div className="max-w-2xl mx-auto mb-8 space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input
                type="text"
                placeholder="Search by cat name, owner, or tribute..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-24 py-3 rounded-full border-stone-200 focus:border-amber-400 focus:ring-amber-400"
                data-testid="memorial-search-input"
              />
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 px-3 py-1 rounded-full text-sm transition-colors ${
                  showFilters || yearFrom || yearTo 
                    ? 'bg-amber-100 text-amber-700' 
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
                {(yearFrom || yearTo) && <span className="w-2 h-2 bg-amber-500 rounded-full" />}
              </button>
            </div>

            {/* Date Range Filters */}
            {showFilters && (
              <Card className="p-4 bg-stone-50 border-stone-200">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-stone-600 whitespace-nowrap">Year Range:</label>
                    <Input
                      type="number"
                      placeholder="From"
                      value={yearFrom}
                      onChange={(e) => { setYearFrom(e.target.value); setPage(1); }}
                      className="w-24 h-9"
                      min="1990"
                      max={new Date().getFullYear()}
                    />
                    <span className="text-stone-400">—</span>
                    <Input
                      type="number"
                      placeholder="To"
                      value={yearTo}
                      onChange={(e) => { setYearTo(e.target.value); setPage(1); }}
                      className="w-24 h-9"
                      min="1990"
                      max={new Date().getFullYear()}
                    />
                  </div>
                  {(yearFrom || yearTo || searchQuery) && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      <X className="w-4 h-4 mr-1" />
                      Clear All
                    </Button>
                  )}
                </div>
                {(yearFrom || yearTo) && (
                  <p className="text-xs text-stone-500 mt-2">
                    Showing memorials from {yearFrom || 'any year'} to {yearTo || 'present'}
                  </p>
                )}
              </Card>
            )}

            {/* Results count */}
            {searchQuery && (
              <p className="text-center text-sm text-stone-500">
                Found {filteredMemorials.length} memorial{filteredMemorials.length !== 1 ? 's' : ''} matching "{searchQuery}"
              </p>
            )}
          </div>
        )}

        {/* Memorials Grid */}
        {loading ? (
          <div className="text-center py-16">
            <Loader2 className="w-10 h-10 animate-spin mx-auto text-stone-400" />
            <p className="text-stone-500 mt-4">Loading memorials...</p>
          </div>
        ) : memorials.length === 0 ? (
          <Card className="max-w-md mx-auto" data-testid="memorials-empty-state">
            <CardContent className="p-12 text-center">
              <Flame className="w-16 h-16 mx-auto text-stone-300 mb-4" />
              <h3 className="text-xl font-semibold text-stone-700 mb-2">No memorials yet</h3>
              <p className="text-stone-500 mb-6">
                Be the first to honor a beloved friend who has crossed the Rainbow Bridge.
              </p>
              <Button 
                onClick={() => setShowSubmitModal(true)}
                className="bg-stone-700 hover:bg-stone-800"
                data-testid="empty-state-share-memorial-button"
              >
                <Heart className="w-4 h-4 mr-2" />
                Share a Memorial
              </Button>
            </CardContent>
          </Card>
        ) : filteredMemorials.length === 0 && searchQuery ? (
          <Card className="max-w-md mx-auto">
            <CardContent className="p-12 text-center">
              <Search className="w-16 h-16 mx-auto text-stone-300 mb-4" />
              <h3 className="text-xl font-semibold text-stone-700 mb-2">No matches found</h3>
              <p className="text-stone-500 mb-4">
                No memorials match "{searchQuery}"
              </p>
              <Button 
                variant="outline"
                onClick={() => setSearchQuery("")}
              >
                Clear Search
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredMemorials.map(memorial => (
              <Card 
                key={memorial.memorial_id}
                className="overflow-hidden hover:shadow-xl transition-all cursor-pointer group bg-white"
                onClick={() => openMemorial(memorial)}
              >
                <div className="aspect-square relative">
                  <img
                    src={`data:${memorial.image_mime_type};base64,${memorial.image_base64}`}
                    alt={memorial.cat_name}
                    className="w-full h-full object-cover grayscale-[30%] group-hover:grayscale-0 transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  
                  {memorial.was_catpals_cat && (
                    <div className="absolute top-3 left-3">
                      <Badge className="bg-amber-600 text-white">
                        <Cat className="w-3 h-3 mr-1" />
                        CatPals+ Family
                      </Badge>
                    </div>
                  )}
                  
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <h3 className="text-xl font-semibold mb-1">{memorial.cat_name}</h3>
                    <p className="text-white/80 text-sm">
                      {formatYears(memorial.birth_year, memorial.passing_year)}
                    </p>
                  </div>
                </div>
                <CardContent className="p-4">
                  <p className="text-stone-600 text-sm line-clamp-2 italic">
                    "{memorial.tribute_message}"
                  </p>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-sm text-stone-500">by {memorial.owner_name}</span>
                    <button 
                      className="flex items-center gap-1 text-amber-600 hover:text-amber-700 transition-colors group"
                      onClick={(e) => { e.stopPropagation(); lightCandle(memorial.memorial_id); }}
                    >
                      <Flame 
                        className={`w-5 h-5 transition-all ${
                          candleAnimating[memorial.memorial_id] 
                            ? 'text-orange-500 scale-125 animate-pulse drop-shadow-[0_0_8px_rgba(251,146,60,0.8)]' 
                            : 'group-hover:scale-110'
                        }`}
                      />
                      <span className={candleAnimating[memorial.memorial_id] ? 'font-bold' : ''}>{memorial.candle_count || 0}</span>
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-8">
            <Button
              variant="outline"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-full"
            >
              Previous
            </Button>
            <span className="text-stone-600">
              Page {pagination.page} of {pagination.pages}
            </span>
            <Button
              variant="outline"
              onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
              disabled={page === pagination.pages}
              className="rounded-full"
            >
              Next
            </Button>
          </div>
        )}

        {/* Quote */}
        <div className="mt-16 text-center">
          <blockquote className="text-xl italic text-stone-500 max-w-2xl mx-auto">
            "Until one has loved an animal, a part of one's soul remains unawakened."
          </blockquote>
          <p className="text-stone-400 mt-2">— Anatole France</p>
        </div>

        {/* Rainbow Bridge Poem */}
        <div className="mt-12 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-8 md:p-12 text-center">
          <Sparkles className="w-10 h-10 text-amber-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-stone-800 mb-4">The Rainbow Bridge</h2>
          <p className="text-stone-600 max-w-2xl mx-auto leading-relaxed">
            Just this side of heaven is a place called Rainbow Bridge. When an animal who has been 
            especially close to someone here passes away, they go to Rainbow Bridge. There are meadows 
            and hills for all of our special friends so they can run and play together...
          </p>
          <p className="text-amber-600 mt-4 font-medium">
            Until we meet again at the Rainbow Bridge 🌈
          </p>
        </div>
      </div>

      {/* Submit Modal */}
      <Dialog open={showSubmitModal} onOpenChange={setShowSubmitModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {submitSuccess ? "Memorial Submitted" : "Share a Memorial"}
            </DialogTitle>
          </DialogHeader>
          
          {submitSuccess ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-stone-600" />
              </div>
              <h3 className="text-xl font-semibold text-stone-800 mb-2">Thank You</h3>
              <p className="text-stone-600 mb-6">
                Your memorial has been submitted for review. Once approved, it will appear on this page 
                so others can honor your beloved friend's memory.
              </p>
              <p className="text-sm text-stone-500 italic mb-6">
                "Those we love don't go away, they walk beside us every day."
              </p>
              <Button onClick={() => setShowSubmitModal(false)} variant="outline">
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="memorial-submit-form">
              {/* Photo */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Photo *</label>
                {formData.image_base64 ? (
                  <div className="relative w-full aspect-video rounded-lg overflow-hidden border bg-stone-100">
                    <img 
                      src={`data:${formData.image_mime_type};base64,${formData.image_base64}`}
                      alt="Preview"
                      className="w-full h-full object-contain"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, image_base64: "", image_mime_type: "" }))}
                      className="absolute top-2 right-2 bg-stone-700 text-white rounded-full p-1.5 hover:bg-stone-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full aspect-video border-2 border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-stone-400 hover:bg-stone-50 transition-colors">
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="memorial-photo-input" />
                    <Camera className="w-10 h-10 text-stone-400 mb-2" />
                    <span className="text-stone-500">Upload a photo of your beloved cat</span>
                    <span className="text-xs text-stone-400 mt-1">Max 10MB</span>
                  </label>
                )}
              </div>

              {/* Cat Name */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Cat's Name *</label>
                <Input
                  value={formData.cat_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, cat_name: e.target.value }))}
                  placeholder="What was their name?"
                  required
                  data-testid="memorial-cat-name-input"
                />
              </div>

              {/* Years */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Birth Year</label>
                  <Input
                    type="number"
                    value={formData.birth_year}
                    onChange={(e) => setFormData(prev => ({ ...prev, birth_year: e.target.value }))}
                    placeholder="e.g., 2015"
                    min="1990"
                    max="2026"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Passing Year</label>
                  <Input
                    type="number"
                    value={formData.passing_year}
                    onChange={(e) => setFormData(prev => ({ ...prev, passing_year: e.target.value }))}
                    placeholder="e.g., 2026"
                    min="1990"
                    max="2026"
                  />
                </div>
              </div>

              {/* Tribute Message */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Tribute Message *</label>
                <Textarea
                  value={formData.tribute_message}
                  onChange={(e) => setFormData(prev => ({ ...prev, tribute_message: e.target.value }))}
                  placeholder="Share a loving memory or tribute to your cat..."
                  rows={4}
                  required
                  data-testid="memorial-tribute-input"
                />
              </div>

              {/* Your Name */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Your Name *</label>
                <Input
                  value={formData.owner_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, owner_name: e.target.value }))}
                  placeholder="Your name"
                  required
                  data-testid="memorial-owner-name-input"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Email (optional)</label>
                <Input
                  type="email"
                  value={formData.owner_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, owner_email: e.target.value }))}
                  placeholder="We'll notify you when approved"
                />
              </div>

              {/* CatPals Cat */}
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                <input
                  type="checkbox"
                  id="catpals"
                  checked={formData.was_catpals_cat}
                  onChange={(e) => setFormData(prev => ({ ...prev, was_catpals_cat: e.target.checked }))}
                  className="w-5 h-5 rounded text-amber-600"
                />
                <label htmlFor="catpals" className="text-stone-700 cursor-pointer">
                  <span className="font-medium">This cat was from CatPals+</span>
                  <span className="block text-sm text-stone-500">Check if you adopted from us</span>
                </label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowSubmitModal(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting || !formData.image_base64 || !formData.cat_name || !formData.owner_name || !formData.tribute_message}
                  className="bg-stone-700 hover:bg-stone-800"
                  data-testid="memorial-submit-button"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Heart className="w-4 h-4 mr-2" />
                      Submit Memorial
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Memorial Detail Modal */}
      <Dialog open={showMemorialModal} onOpenChange={setShowMemorialModal}>
        <DialogContent className="max-w-2xl">
          {selectedMemorial && (
            <div className="space-y-4">
              <div className="rounded-lg overflow-hidden bg-stone-100">
                <img
                  src={`data:${selectedMemorial.image_mime_type};base64,${selectedMemorial.image_base64}`}
                  alt={selectedMemorial.cat_name}
                  className="w-full max-h-[400px] object-contain"
                />
              </div>
              
              <div className="text-center">
                <h3 className="text-2xl font-semibold text-stone-800">{selectedMemorial.cat_name}</h3>
                <p className="text-stone-500">
                  {formatYears(selectedMemorial.birth_year, selectedMemorial.passing_year)}
                </p>
                {selectedMemorial.was_catpals_cat && (
                  <Badge className="bg-amber-600 text-white mt-2">
                    <Cat className="w-3 h-3 mr-1" />
                    CatPals+ Family
                  </Badge>
                )}
              </div>
              
              <blockquote className="text-center text-stone-600 italic text-lg p-4 bg-stone-50 rounded-lg">
                "{selectedMemorial.tribute_message}"
              </blockquote>
              
              <p className="text-center text-stone-500">
                Remembered with love by {selectedMemorial.owner_name}
              </p>
              
              <div className="flex justify-center">
                <button 
                  className="flex items-center gap-2 px-6 py-3 bg-amber-100 text-amber-700 rounded-full hover:bg-amber-200 transition-colors group"
                  onClick={() => lightCandle(selectedMemorial.memorial_id)}
                >
                  <Flame 
                    className={`w-6 h-6 transition-all ${
                      candleAnimating[selectedMemorial.memorial_id] 
                        ? 'text-orange-500 scale-125 animate-pulse drop-shadow-[0_0_12px_rgba(251,146,60,0.9)]' 
                        : 'group-hover:scale-110'
                    }`}
                  />
                  <span className="font-medium">Light a Candle</span>
                  <span className={`text-amber-600 ${candleAnimating[selectedMemorial.memorial_id] ? 'font-bold' : ''}`}>
                    ({selectedMemorial.candle_count || 0})
                  </span>
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InMemoriamPage;
